/**
 * In-memory engine used for development, demos and tests (`?engine=mock`, or when the real engine fails to load).
 * It implements the EngineTransport contract over the seed data in ./mock-data.ts. Behaviour is deliberately simple:
 * enough to exercise every screen, not a faithful copy of the real engine.
 */
import {
  ENGINE_ERROR_CODES,
  EngineRpcError,
  SEARCH_KEYWORD_ALIASES,
  SEARCH_KEYWORD_LABELS,
  SEARCH_KEYWORDS,
  dateValueWords,
  automaticCadenceForScore,
  CADENCE_DAYS,
  initialsOf,
  newId,
  nowIso,
  toCsv,
  type Contact,
  type ContactDetail,
  type ContactProfile,
  type ContactRow,
  type ContactQuery,
  type EngineMethodName,
  type EngineMethods,
  type EngineNotification,
  type EngineTransport,
  type Group,
  type Highlight,
  type Id,
  type Note,
  type Page,
  type ParsedSearch,
  type PotentialDuplicate,
  type ReconnectCadenceState,
  type Reminder,
  type SearchNode,
  type SyncStatus,
  type TimelineEntry,
  type TimelineItem,
  type UserNotificationSettings,
  type UserSettings,
  type WorkspaceSettings,
  type ContactLookup,
  type Lookup,
  type MilestoneDate,
  type Need,
  type Service,
  type MatchCard,
  type ReconnectCadence,
  type CustomFieldDefinition,
  type ContactFieldValue,
  type ContactMerge,
  type ConnectionGraph,
  type ConnectionLink,
} from "@hub/shared";
import { buildSeed, hlc, MOCK_NOW, MOCK_USER_ID, MOCK_WORKSPACE_ID, mockUser, mockWorkspace, nextOccurrence, type MockSeed, type SeedInteraction } from "./mock-data";

type Handler<M extends EngineMethodName> = (params: EngineMethods[M]["params"]) => EngineMethods[M]["result"] | Promise<EngineMethods[M]["result"]>;
type Handlers = { [M in EngineMethodName]?: Handler<M> };

const LATENCY_MS = 25;

export class MockEngine implements EngineTransport {
  private seed: MockSeed;
  private listeners = new Set<(n: EngineNotification) => void>();
  private timelineState = new Map<string, { seen: boolean; status: TimelineItem["status"]; snoozedUntil: string | null; dismissedContacts: Set<Id> }>();
  private duplicates: PotentialDuplicate[];
  private recentSearches: string[] = ["company:northwind", "relationship:closest", "need:cfo"];
  private userSettings: UserSettings = { userId: MOCK_USER_ID, theme: "auto", darkTheme: "dark-graphite", appIcon: null, showNotesInSidebar: true, reminderParsingDisabled: false, defaultReminderTime: "09:00", metadata: {} };
  private notificationSettings: UserNotificationSettings = {
    userId: MOCK_USER_ID, workspaceId: MOCK_WORKSPACE_ID, preMeeting: true, inMeeting: false, postMeeting: true, dailyBrief: true, weeklyDigest: true, birthdays: true, reconnects: true, includeRecent: true, includeRecurringEvents: false, includePeopleWithoutInformation: false, feedBirthdays: true, feedReconnects: true, feedNews: true, feedEvents: true, feedGroupReconnects: true, feedPosts: false, feedProfileChanges: true, feedMatches: true,
  };
  private workspaceSettings: WorkspaceSettings = { workspaceId: MOCK_WORKSPACE_ID, reconnectPerDay: 3, automergeEnabled: true };
  private syncStatus: SyncStatus = { phase: "idle", lastPullAt: MOCK_NOW.toISOString(), lastPushAt: MOCK_NOW.toISOString(), pendingOps: 0, error: null, errorCode: null, initialSyncCompleted: true };
  private snoozedReconnects = new Map<Id, string>();
  private customFields: CustomFieldDefinition[] = [{ workspaceId: MOCK_WORKSPACE_ID, fieldName: "how we met", displayName: "How we met", fieldType: "text", options: [], hlc: hlc(MOCK_NOW.toISOString()) }];
  private matchCards: MatchCard[] = [];
  private merges: ContactMerge[] = [];
  private newsScannedAt: string | null = null;
  private selfContactId: Id | null = null;
  private initialised = false;
  private now: () => Date;

  constructor(opts: { now?: () => Date } = {}) {
    this.seed = buildSeed();
    this.now = opts.now ?? (() => new Date());
    this.duplicates = [
      { id: "dup-1", workspaceId: MOCK_WORKSPACE_ID, status: "pending", score: 1, canAutomerge: true, source: "exact_name", contactIds: ["c-001", "c-039"], removedContactIds: [], hlc: hlc(MOCK_NOW.toISOString()), created: MOCK_NOW.toISOString(), updated: MOCK_NOW.toISOString() },
      { id: "dup-2", workspaceId: MOCK_WORKSPACE_ID, status: "pending", score: 0.82, canAutomerge: false, source: "fuzzy_name", contactIds: ["c-002", "c-040"], removedContactIds: [], hlc: hlc(MOCK_NOW.toISOString()), created: MOCK_NOW.toISOString(), updated: MOCK_NOW.toISOString() },
    ];
  }

  subscribe(listener: (n: EngineNotification) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(n: EngineNotification) {
    for (const l of this.listeners) l(n);
  }

  async invoke<M extends EngineMethodName>(method: M, params: EngineMethods[M]["params"]): Promise<EngineMethods[M]["result"]> {
    const handler = this.handlers[method] as Handler<M> | undefined;
    if (!handler) throw new EngineRpcError(`Method not implemented in mock: ${method}`, ENGINE_ERROR_CODES.methodNotFound);
    if (LATENCY_MS > 0 && typeof window !== "undefined" && !(globalThis as { __HUB_TEST__?: boolean }).__HUB_TEST__) {
      await new Promise((r) => setTimeout(r, LATENCY_MS));
    }
    const result = await handler(params);
    return clone(result);
  }

  // ------------------------------------------------------------------ helpers

  private profile(id: Id): ContactProfile {
    const p = this.seed.profiles.get(id);
    if (!p) throw new EngineRpcError("Contact not found", ENGINE_ERROR_CODES.notFound);
    return p;
  }

  private contact(id: Id): Contact {
    const c = this.seed.contacts.find((x) => x.id === id && !x.deleted);
    if (!c) throw new EngineRpcError("Contact not found", ENGINE_ERROR_CODES.notFound);
    return c;
  }

  private lookupsFor(contactId: Id): (ContactLookup & { lookup: Lookup })[] {
    return this.seed.contactLookups
      .filter((cl) => cl.contactId === contactId && !cl.deleted)
      .map((cl) => ({ ...cl, lookup: this.seed.lookups.find((l) => l.id === cl.lookupId)! }))
      .filter((x) => x.lookup);
  }

  private groupIdsFor(contactId: Id): Id[] {
    const staticIds = this.seed.contactGroups.filter((cg) => cg.contactId === contactId).map((cg) => cg.groupId);
    const smart = this.seed.groups.filter((g) => g.kind === "smart" && g.filter && !g.deleted && this.matches(contactId, parse(g.filter).ast)).map((g) => g.id);
    return Array.from(new Set([...staticIds, ...smart]));
  }

  private memberIds(group: Group): Id[] {
    if (group.id === "g-fav") return Array.from(this.seed.starred);
    if (group.kind === "smart" && group.filter) {
      const ast = parse(group.filter).ast;
      return this.seed.contacts.filter((c) => !c.deleted && !c.isArchived && this.matches(c.id, ast)).map((c) => c.id);
    }
    return this.seed.contactGroups.filter((cg) => cg.groupId === group.id).map((cg) => cg.contactId);
  }

  row(contactId: Id): ContactRow {
    return { ...this.baseRow(contactId), groupIds: this.groupIdsFor(contactId) };
  }

  /** Row without group membership; smart-group evaluation uses this to avoid recursing through groupIdsFor. */
  private baseRow(contactId: Id): ContactRow {
    const c = this.contact(contactId);
    const p = this.profile(contactId);
    const st = this.seed.statistics.get(contactId);
    const lookups = this.lookupsFor(contactId);
    const email = lookups.find((l) => l.lookup.type === "email" && l.isPrimary) ?? lookups.find((l) => l.lookup.type === "email");
    const phone = lookups.find((l) => l.lookup.type === "phone");
    const lastInteraction = this.seed.interactions.filter((i) => i.contactIds.includes(contactId) && i.date <= this.now().toISOString()).sort((a, b) => (a.date < b.date ? 1 : -1))[0];
    const company = p.workHistory.find((w) => w.isCurrent)?.company ?? p.workHistory[0]?.company ?? null;
    return {
      contactId,
      displayName: p.displayName,
      firstName: p.firstName,
      lastName: p.lastName,
      headline: p.headline,
      company,
      location: p.addresses[0]?.city ?? null,
      avatarUrl: p.avatarUrl,
      initials: initialsOf(p.displayName),
      relationship: c.relationship,
      bucket: st?.bucket ?? 4,
      score: st?.score ?? 0,
      lastInteractionDate: lastInteraction?.date ?? null,
      lastInteractionType: lastInteraction?.type ?? null,
      nextEventDate: st?.nextEventDate ?? null,
      isStarred: this.seed.starred.has(contactId),
      isArchived: c.isArchived,
      isHubUser: !!c.linkedUserId,
      hasNeeds: this.seed.needs.some((n) => n.contactId === contactId && !n.isCompleted && !n.deleted),
      hasServices: this.seed.services.some((s) => s.contactId === contactId && s.isActive && !s.deleted),
      groupIds: [],
      primaryEmail: email?.lookup.value ?? null,
      primaryPhone: phone?.lookup.value ?? null,
      birthday: p.birthday ? { month: p.birthday.month, day: p.birthday.day } : null,
      updated: c.updated,
    };
  }

  private matches(contactId: Id, node: SearchNode): boolean {
    const row = this.baseRow(contactId);
    const p = this.profile(contactId);
    const c = this.contact(contactId);
    const st = this.seed.statistics.get(contactId);
    const notes = this.seed.notes.filter((n) => n.contactId === contactId && !n.deleted);
    const needs = this.seed.needs.filter((n) => n.contactId === contactId && !n.deleted);
    const services = this.seed.services.filter((s) => s.contactId === contactId && !s.deleted);
    const has = (s: string | null | undefined, v: string) => (s ?? "").toLowerCase().includes(v.toLowerCase());
    // The engine's semantics: "<" is before the range starts, ">" after it ends, "=" inside it; ">=" and "<=" include it.
    const cmpDate = (d: string | null, op: string, v: string) => {
      if (!d) return false;
      const range = resolveDate(v, this.now());
      if (!range) return false;
      if (op === "gte") return d >= range.start;
      if (op === "gt") return d >= range.end;
      if (op === "lte") return d < range.end;
      if (op === "lt") return d < range.start;
      return d >= range.start && d < range.end;
    };
    const cmpBirthday = (b: { month: number; day: number } | null | undefined, op: string, v: string) => {
      if (!b) return false;
      const month = Number(v);
      if (Number.isInteger(month) && month >= 1 && month <= 12) return b.month === month;
      const range = resolveDate(v, this.now());
      if (!range) return false;
      const md = b.month * 100 + b.day;
      const s = new Date(range.start);
      const e = new Date(new Date(range.end).getTime() - 1);
      const from = (s.getUTCMonth() + 1) * 100 + s.getUTCDate();
      const to = (e.getUTCMonth() + 1) * 100 + e.getUTCDate();
      if (op === "gte" || op === "gt") return md >= from;
      if (op === "lte" || op === "lt") return md <= to;
      return from <= to ? md >= from && md <= to : md >= from || md <= to;
    };
    const cmpNum = (n: number, op: string, v: string) => {
      const t = Number(v);
      if (!Number.isFinite(t)) return false;
      return op === "gte" ? n >= t : op === "lte" ? n <= t : n === t;
    };
    switch (node.kind) {
      case "all":
        return true;
      case "and":
        return node.children.every((ch) => this.matches(contactId, ch));
      case "or":
        return node.children.some((ch) => this.matches(contactId, ch));
      case "not":
        return !this.matches(contactId, node.child);
      case "text": {
        const v = node.value.toLowerCase();
        return (
          has(p.displayName, v) || has(p.headline, v) || has(p.bio, v) || has(row.company, v) || has(row.location, v) ||
          p.workHistory.some((w) => has(w.company, v) || has(w.title, v)) || p.education.some((e) => has(e.school, v)) ||
          notes.some((n) => has(n.plainText, v)) || needs.some((n) => has(n.title, v)) || services.some((s) => has(s.title, v)) || p.interests.some((i) => has(i, v))
        );
      }
      case "existence": {
        const present = (() => {
          switch (node.target) {
            case "email": return !!row.primaryEmail;
            case "phone": return !!row.primaryPhone;
            case "notes": return notes.length > 0;
            case "name": return !!p.firstName;
            case "location": return !!row.location;
            case "birthday": return !!p.birthday;
            case "company": return !!row.company;
            case "title": return p.workHistory.some((w) => w.title);
            case "website": return p.websites.length > 0;
            case "social": return p.socialLinks.length > 0;
            case "avatar": return !!p.avatarUrl;
            case "need": return needs.length > 0;
            case "service": return services.length > 0;
          }
        })();
        return node.negate ? !present : present;
      }
      case "keyword": {
        const { keyword, operator: op, value: v } = node;
        switch (keyword) {
          case "company": return p.workHistory.some((w) => has(w.company, v));
          case "title": return p.workHistory.some((w) => has(w.title, v)) || has(p.headline, v);
          case "education": return p.education.some((e) => has(e.school, v) || has(e.field, v));
          case "interest": return p.interests.some((i) => has(i, v));
          case "location": return has(row.location, v) || p.addresses.some((a) => has(a.city, v) || has(a.country, v));
          case "email": return v === "*" ? !!row.primaryEmail : has(row.primaryEmail, v);
          case "phone": return v === "*" ? !!row.primaryPhone : has(row.primaryPhone, v);
          case "name": return has(p.displayName, v);
          case "bio": return has(p.bio, v);
          case "notes": return notes.some((n) => has(n.plainText, v));
          case "emailcount": return cmpNum(st?.emailCount ?? 0, op, v);
          case "meetingcount": return cmpNum(st?.eventCount ?? 0, op, v);
          case "messagecount": return cmpNum(st?.messageCount ?? 0, op, v);
          case "callcount": return cmpNum(st?.callCount ?? 0, op, v);
          case "groupcount": return cmpNum(this.seed.contactGroups.filter((cg) => cg.contactId === contactId).length, op, v);
          case "lastmet": case "lastevent": return cmpDate(st?.lastEventDate ?? null, op, v);
          case "firstmet": case "firstevent": return cmpDate(st?.firstEventDate ?? null, op, v);
          case "lastemail": return cmpDate(st?.lastEmailDate ?? null, op, v);
          case "firstemail": return cmpDate(st?.firstEmailDate ?? null, op, v);
          case "lasttext": return cmpDate(st?.lastMessageDate ?? null, op, v);
          case "firsttext": return cmpDate(st?.firstMessageDate ?? null, op, v);
          case "nextmeeting": return cmpDate(st?.nextEventDate ?? null, op, v);
          case "lastcontact": return cmpDate(row.lastInteractionDate, op, v);
          case "created": return cmpDate(c.created, op, v);
          case "updated": return cmpDate(c.updated, op, v);
          case "lastnote": case "notedate": return notes.some((n) => cmpDate(n.created, op, v));
          case "birthday": return v === "*" ? !!p.birthday : cmpBirthday(p.birthday, op, v);
          case "is_local": return c.isLocal === (v !== "false");
          case "is_memorialized": return c.isMemorialized === (v !== "false");
          case "is_archived": return c.isArchived === (v !== "false");
          case "source": return has(c.originSource, v);
          case "integration": return this.lookupsFor(contactId).some((l) => has(l.source, v));
          case "twitter": case "linkedin": case "facebook": case "instagram": return p.socialLinks.some((s) => s.platform === keyword && (v === "*" || has(s.handle, v)));
          case "group": {
            const g = this.seed.groups.find((g) => has(g.name, v) || has(g.slug, v) || g.id === v);
            return !!g && this.memberIds(g).includes(contactId);
          }
          case "relationship": {
            const tierByName: Record<string, number> = { closest: 1, closer: 2, close: 3, none: 4 };
            const want = tierByName[v.toLowerCase()] ?? Number(v);
            return row.bucket === want;
          }
          case "need": return needs.some((n) => v === "*" || has(n.title, v) || has(n.description, v) || n.keywords.some((k) => has(k, v)));
          case "service": return services.some((s) => v === "*" || has(s.title, v) || has(s.shortDescription, v) || s.keywords.some((k) => has(k, v)));
          case "category": return needs.some((n) => has(n.category, v)) || services.some((s) => has(s.category, v));
          case "hub": return row.isHubUser === (v !== "false");
        }
        return false;
      }
    }
  }

  private sortRows(rows: ContactRow[], sort: ContactQuery["sort"] = "name", direction: "asc" | "desc" = "asc"): ContactRow[] {
    const dir = direction === "asc" ? 1 : -1;
    const key = (r: ContactRow): string | number => {
      switch (sort) {
        case "last_interaction": return r.lastInteractionDate ?? "";
        case "first_interaction": return this.seed.statistics.get(r.contactId)?.firstInteractionDate ?? "";
        case "interaction_count": { const s = this.seed.statistics.get(r.contactId); return s ? s.emailCount + s.eventCount + s.messageCount + s.callCount : 0; }
        case "score": return r.score;
        case "created": return this.contact(r.contactId).created;
        case "updated": return r.updated;
        case "next_event": return r.nextEventDate ?? "9999";
        default: return r.displayName.toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      return (ka < kb ? -1 : ka > kb ? 1 : 0) * dir;
    });
  }

  private page<T>(items: T[], offset = 0, limit = 50): Page<T> {
    const slice = items.slice(offset, offset + limit);
    return { items: slice, total: items.length, nextOffset: offset + slice.length < items.length ? offset + slice.length : null };
  }

  private entry(i: SeedInteraction): TimelineEntry {
    const base = { id: i.id, workspaceId: MOCK_WORKSPACE_ID, type: i.type, date: i.date, integrationId: i.type === "email" ? "int-google" : i.type === "event" ? "int-calendar" : i.type === "message_day" ? "int-apple-messages" : null, hlc: hlc(i.date), created: i.date, updated: i.date };
    const participants = i.contactIds.map((cid) => ({ id: `p-${i.id}-${cid}`, workspaceId: MOCK_WORKSPACE_ID, interactionId: i.id, lookupId: this.lookupsFor(cid)[0]?.lookupId ?? "", role: "to" as const, name: this.profile(cid).displayName, contactId: cid }));
    const refs = participants.map((p) => ({ id: p.id, lookupId: p.lookupId, lookupType: "email" as const, value: this.row(p.contactId).primaryEmail ?? "", role: p.role, name: p.name }));
    const e: TimelineEntry = { interaction: { ...base, participants: refs }, participants, note: null, isStarred: false };
    if (i.type === "email") e.email = { interactionId: i.id, providerId: null, threadId: null, subject: i.subject ?? null, snippet: "Thanks for the update, let me take a look and get back to you by Thursday.", isCalendar: false, emailAccountId: "you@example.com", sender: i.isFromMe ? "you@example.com" : (this.row(i.contactIds[0]).primaryEmail ?? null), isFromMe: !!i.isFromMe };
    if (i.type === "event") e.event = { interactionId: i.id, title: i.title ?? null, description: null, location: null, allDay: false, startDatetime: i.startDatetime ?? i.date, endDatetime: i.endDatetime ?? null, eventId: null, calendarId: null, url: null, isRecurring: false, ownerEmail: null, participantCount: i.contactIds.length + 1 };
    if (i.type === "message_day") e.messageDay = { interactionId: i.id, platform: "imessage", messageCount: i.messageCount ?? 4, messagesFromMe: Math.ceil((i.messageCount ?? 4) / 2), messagesToMe: Math.floor((i.messageCount ?? 4) / 2), groupMessagesFromMe: 0, groupMessagesToMe: 0, firstMessageFromMe: i.date, firstMessageToMe: i.date, lastMessageFromMe: i.date, lastMessageToMe: i.date };
    if (i.type === "call") e.call = { interactionId: i.id, providerCallId: i.id, startDatetime: i.date, endDatetime: new Date(new Date(i.date).getTime() + 12 * 60_000).toISOString(), answered: true, outgoing: !!i.outgoing, mediaType: "audio", isGroup: false };
    return e;
  }

  private cadenceState(contactId: Id): ReconnectCadenceState {
    const row = this.seed.cadences.find((c) => c.contactId === contactId && !c.deleted) ?? null;
    if (!row) return { state: "auto", row: null };
    if (row.isDisabled) return { state: "disabled", row };
    if (row.isAutomatic || !row.cadence) return { state: "auto", row };
    return { state: row.cadence, row, qualifier: row.qualifier };
  }

  private highlights(contactId: Id): Highlight[] {
    const row = this.row(contactId);
    const p = this.profile(contactId);
    const st = this.seed.statistics.get(contactId);
    const out: Highlight[] = [];
    const total = st ? st.emailCount + st.eventCount + st.messageCount + st.callCount : 0;
    if (total > 0) out.push({ kind: "relationship", text: `${total} interactions since ${new Date(st!.firstInteractionDate!).toLocaleDateString(undefined, { month: "short", year: "numeric" })}; last one ${relative(row.lastInteractionDate, this.now())}.` });
    else out.push({ kind: "relationship", text: "No recorded interactions yet." });
    const work = p.workHistory.find((w) => w.isCurrent);
    if (work) out.push({ kind: "work", text: `${work.title ?? "Works"} at ${work.company}${p.workHistory.length > 1 ? `, previously ${p.workHistory[1].company}` : ""}.` });
    const notes = this.seed.notes.filter((n) => n.contactId === contactId && !n.deleted);
    if (notes.length) out.push({ kind: "notes", text: notes[0].plainText.split(".")[0] + "." });
    if (p.interests.length) out.push({ kind: "interests", text: `Interested in ${p.interests.join(" and ")}.` });
    if (p.birthday) out.push({ kind: "milestone", text: `Birthday on ${monthName(p.birthday.month)} ${p.birthday.day}.` });
    const related = this.seed.related.filter((r) => r.contactId === contactId);
    if (related.length) out.push({ kind: "mutual", text: `Connected to ${related.map((r) => this.profile(r.relatedContactId).displayName).join(", ")}.`, contactIds: related.map((r) => r.relatedContactId) });
    const needs = this.seed.needs.filter((n) => n.contactId === contactId && !n.isCompleted && !n.deleted);
    if (needs.length) out.push({ kind: "network", text: `Currently needs: ${needs.map((n) => n.title).join("; ")}.` });
    return out;
  }

  private detail(contactId: Id): ContactDetail {
    const contact = this.contact(contactId);
    const profile = this.profile(contactId);
    return {
      contact,
      profile,
      lookups: this.lookupsFor(contactId),
      statistics: this.seed.statistics.get(contactId) ?? null,
      groups: this.groupIdsFor(contactId).map((gid) => this.seed.groups.find((g) => g.id === gid)!).filter(Boolean),
      notes: this.seed.notes.filter((n) => n.contactId === contactId && !n.deleted).sort((a, b) => (a.created < b.created ? 1 : -1)),
      reminders: this.seed.reminders.filter((r) => r.contactId === contactId && !r.deleted).sort((a, b) => (a.fireAt < b.fireAt ? -1 : 1)),
      cadence: this.cadenceState(contactId),
      related: this.seed.related.filter((r) => r.contactId === contactId && !r.deleted).map((r) => ({ ...r, labels: this.seed.relatedLabels.filter((l) => l.contactId === contactId && l.relatedContactId === r.relatedContactId && !l.deleted).map((l) => l.label), profile: this.profile(r.relatedContactId) })),
      milestones: this.seed.milestones.filter((m) => m.contactId === contactId && !m.deleted),
      avatars: this.avatarsFor(contactId),
      needs: this.seed.needs.filter((n) => n.contactId === contactId && !n.deleted),
      services: this.seed.services.filter((s) => s.contactId === contactId && !s.deleted),
      highlights: this.highlights(contactId),
      isStarred: this.seed.starred.has(contactId),
    };
  }

  private avatarsFor(contactId: Id) {
    const p = this.profile(contactId);
    const base = { workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(p.dataUpdated ?? MOCK_NOW.toISOString()), created: MOCK_NOW.toISOString(), updated: MOCK_NOW.toISOString(), deleted: null, contactId, lookupId: null, thumbnailUrl: null, score: null };
    const n = Number(new URL(p.avatarUrl ?? "https://i.pravatar.cc/150?img=1").searchParams.get("img") ?? 1);
    return [
      { ...base, id: `av-${contactId}`, url: p.avatarUrl ?? `https://i.pravatar.cc/150?img=${n}`, source: "google" },
      { ...base, id: `av-${contactId}-2`, url: `https://i.pravatar.cc/150?img=${(n % 70) + 1}`, source: "linkedin" },
      { ...base, id: `av-${contactId}-3`, url: `https://i.pravatar.cc/150?img=${((n + 7) % 70) + 1}`, source: "apple_contacts" },
    ];
  }

  private touch(contactId: Id) {
    const c = this.contact(contactId);
    c.updated = nowIso();
    c.hlc = hlc(c.updated);
    this.emit({ method: "contactsChanged", params: { contactIds: [contactId] } });
  }

  private timelineItems(): (TimelineItem & { contacts: ContactRow[] })[] {
    const items: (TimelineItem & { contacts: ContactRow[] })[] = [];
    const today = this.now().toISOString().slice(0, 10);
    const mk = (itemType: TimelineItem["itemType"], itemKey: string, itemDate: string, title: string, body: string | null, contactIds: Id[], source: string | null = null, metadata: Record<string, unknown> = {}) => {
      const st = this.timelineState.get(itemKey);
      const contacts = contactIds.filter((id) => !st?.dismissedContacts.has(id)).map((id) => this.row(id));
      items.push({ id: itemKey, workspaceId: MOCK_WORKSPACE_ID, userId: MOCK_USER_ID, itemType, itemKey, itemDate, title, body, contactIds: contacts.map((c) => c.contactId), source, metadata, seen: st?.seen ?? false, status: st?.status ?? "active", snoozedUntil: st?.snoozedUntil ?? null, contacts });
    };
    const suggestions = this.reconnectSuggestions();
    if (suggestions.members.length) mk("reconnect", `reconnect:${today}`, this.now().toISOString(), "People to reconnect with", "Based on how often you usually talk.", suggestions.members.map((m) => m.contactId), null, { reasons: Object.fromEntries(suggestions.members.map((m) => [m.contactId, m.reason])) });
    for (const b of this.upcomingBirthdays(14)) mk("birthday", `birthday:${b.contactId}:${this.now().getUTCFullYear()}`, b.nextOccurrence!, `${this.profile(b.contactId).displayName} has a birthday ${relativeDay(b.nextOccurrence!, this.now())}`, null, [b.contactId]);
    for (const r of this.seed.reminders.filter((r) => !r.isProcessed && !r.deleted && r.fireAt <= this.now().toISOString())) mk("reminder", `reminder:${r.id}`, r.fireAt, r.title ?? "Reminder", r.contactId ? `About ${this.profile(r.contactId).displayName}` : null, r.contactId ? [r.contactId] : [], null, { reminderId: r.id });
    for (const e of this.seed.interactions.filter((i) => i.type === "event" && i.date > this.now().toISOString() && i.date < new Date(this.now().getTime() + 7 * 86_400_000).toISOString())) mk("upcoming_event", `event:${e.id}`, e.date, e.title ?? "Meeting", `${new Date(e.date).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}`, e.contactIds, "apple_calendar", { interactionId: e.id });
    mk("profile_change", "diff:c-005:title", new Date(this.now().getTime() - 2 * 86_400_000).toISOString(), "Grace Whitfield has a new title", "VP Engineering at Atlas Robotics (was Director of Engineering)", ["c-005"], "linkedin", { fieldName: "title", oldValue: "Director of Engineering", newValue: "VP Engineering" });
    mk("news", "news:1", new Date(this.now().getTime() - 3 * 86_400_000).toISOString(), "Northwind Labs raises a seed round", "TechPress: Northwind Labs announced a 4M seed round led by Brightline Capital.", ["c-001", "c-002"], "news", { url: "https://example.com/news/northwind" });
    mk("import", "import:apple:1", new Date(this.now().getTime() - 5 * 86_400_000).toISOString(), "Apple Contacts import finished", "38 people added, 2 updated.", [], "apple_contacts");
    if (this.duplicates.some((d) => d.status === "pending")) mk("duplicate", "duplicates:pending", new Date(this.now().getTime() - 86_400_000).toISOString(), `${this.duplicates.filter((d) => d.status === "pending").length} possible duplicates to review`, "Merging keeps everything from both records.", this.duplicates.filter((d) => d.status === "pending").flatMap((d) => d.contactIds));
    mk("whats_new", "whats-new:1", new Date(this.now().getTime() - 6 * 86_400_000).toISOString(), "Needs and services are here", "Record what people need and what they offer. Hub finds the matches inside your own network.", []);
    return items.sort((a, b) => (a.itemDate < b.itemDate ? 1 : -1));
  }

  private reconnectSuggestions() {
    const now = this.now();
    const candidates = this.seed.contacts
      .filter((c) => !c.deleted && !c.isArchived && !(this.snoozedReconnects.get(c.id) && this.snoozedReconnects.get(c.id)! > now.toISOString()))
      .map((c) => {
        const row = this.row(c.id);
        const cadence = this.cadenceState(c.id);
        if (cadence.state === "disabled") return null;
        const name = cadence.state === "auto" ? automaticCadenceForScore(row.score) : cadence.state;
        const days = CADENCE_DAYS[name];
        const since = row.lastInteractionDate ? Math.floor((now.getTime() - new Date(row.lastInteractionDate).getTime()) / 86_400_000) : 9999;
        if (since < days || row.score < 0.03) return null;
        return { contactId: c.id, cadence: cadence.state === "auto" ? ("auto" as const) : name, reason: `${row.lastInteractionDate ? `Last talked ${since} days ago` : "Never talked"}; usually every ${name === "weekly" ? "week" : name === "monthly" ? "month" : name === "quarterly" ? "quarter" : "year"}.`, overdue: since - days };
      })
      .filter((x): x is NonNullable<typeof x> => !!x)
      .sort((a, b) => b.overdue - a.overdue)
      .slice(0, this.workspaceSettings.reconnectPerDay);
    return { id: `rs-${now.toISOString().slice(0, 10)}`, workspaceId: MOCK_WORKSPACE_ID, userId: MOCK_USER_ID, localDate: now.toISOString().slice(0, 10), suggestedAt: now.toISOString(), members: candidates.map(({ contactId, cadence, reason }) => ({ contactId, cadence, reason })) };
  }

  private upcomingBirthdays(days: number): (MilestoneDate & { contact: ContactRow })[] {
    const now = this.now();
    const until = new Date(now.getTime() + days * 86_400_000).toISOString();
    return this.seed.milestones
      .filter((m) => m.dateType === "birthday" && !m.deleted && !this.contact(m.contactId).isArchived)
      .map((m) => ({ ...m, nextOccurrence: nextOccurrence(m.month, m.day, now) }))
      .filter((m) => m.nextOccurrence! <= until)
      .sort((a, b) => (a.nextOccurrence! < b.nextOccurrence! ? -1 : 1))
      .map((m) => ({ ...m, contact: this.row(m.contactId) }));
  }

  /** The same shape the engine builds: shared meetings and threads, related labels, shared groups, Hub links. */
  private connectionGraph(contactId: Id, limit: number): ConnectionGraph {
    if (!this.seed.profiles.has(contactId)) throw new EngineRpcError("Contact not found", ENGINE_ERROR_CODES.notFound);
    const live = (id: Id) => { const c = this.seed.contacts.find((x) => x.id === id); return Boolean(c && !c.deleted && !c.isArchived); };
    const isSelf = contactId === this.selfContactId;
    const shared = new Map<Id, { events: number; emails: number; lastDate: string | null }>();
    const bump = (id: Id, type: string, date: string) => {
      const s = shared.get(id) ?? { events: 0, emails: 0, lastDate: null };
      if (type === "event") s.events++; else s.emails++;
      if (!s.lastDate || date > s.lastDate) s.lastDate = date;
      shared.set(id, s);
    };
    for (const i of this.seed.interactions) {
      if (i.type !== "event" && i.type !== "email") continue;
      if (isSelf) for (const id of i.contactIds) { if (id !== contactId && live(id)) bump(id, i.type, i.date); }
      else if (i.contactIds.includes(contactId)) for (const id of i.contactIds) { if (id !== contactId && id !== this.selfContactId && live(id)) bump(id, i.type, i.date); }
    }
    const labels = new Map<Id, string[]>();
    for (const r of this.seed.related.filter((r) => r.contactId === contactId && !r.deleted && live(r.relatedContactId))) labels.set(r.relatedContactId, this.seed.relatedLabels.filter((l) => l.contactId === contactId && l.relatedContactId === r.relatedContactId && !l.deleted).map((l) => l.label));
    const groups = new Map<Id, string[]>();
    for (const gid of this.groupIdsFor(contactId)) {
      const g = this.seed.groups.find((x) => x.id === gid);
      if (!g || g.isInternal || g.deleted) continue;
      for (const id of this.memberIds(g)) if (id !== contactId && live(id)) groups.set(id, [...(groups.get(id) ?? []), g.name]);
    }
    const hub = new Set(this.seed.contacts.filter((c) => c.linkedUserId && live(c.id)).map((c) => c.id));
    if (isSelf) for (const id of hub) if (id !== contactId && !shared.has(id)) shared.set(id, { events: 0, emails: 0, lastDate: null });
    const ids = new Set<Id>([...shared.keys(), ...labels.keys(), ...groups.keys()]);
    ids.delete(contactId);
    if (this.selfContactId && !isSelf) ids.delete(this.selfContactId);
    const all = [...ids].map((id) => {
      const s = shared.get(id) ?? { events: 0, emails: 0, lastDate: null };
      const l = labels.get(id) ?? [];
      const g = groups.get(id) ?? [];
      const onHub = hub.has(id);
      return { contact: this.row(id), events: s.events, emails: s.emails, lastDate: s.lastDate, labels: l, groups: g, onHub, weight: s.events * 2 + s.emails + l.length * 3 + g.length + (onHub ? 1 : 0) };
    }).sort((a, b) => b.weight - a.weight || (b.lastDate ?? "").localeCompare(a.lastDate ?? ""));
    const nodes = all.slice(0, Math.max(1, limit));
    const kept = new Set(nodes.map((n) => n.contact.contactId));
    const pair = new Map<string, ConnectionLink>();
    for (const i of this.seed.interactions) {
      if (i.type !== "event" && i.type !== "email") continue;
      const members = i.contactIds.filter((id) => kept.has(id));
      for (let x = 0; x < members.length; x++) for (let y = x + 1; y < members.length; y++) {
        const [a, b] = [members[x], members[y]].sort();
        const l = pair.get(`${a}|${b}`) ?? { a, b, count: 0 };
        l.count++;
        pair.set(`${a}|${b}`, l);
      }
    }
    return { center: this.row(contactId), nodes, links: [...pair.values()].sort((x, y) => y.count - x.count), more: all.length - nodes.length, isSelf };
  }

  private searchRows(q: string | undefined, includeArchived = false): ContactRow[] {
    const ast = parse(q ?? "").ast;
    return this.seed.contacts.filter((c) => !c.deleted && (includeArchived || !c.isArchived) && this.matches(c.id, ast)).map((c) => this.row(c.id));
  }

  private applyInput(contactId: Id, input: EngineMethods["createContact"]["params"]) {
    const p = this.profile(contactId);
    const c = this.contact(contactId);
    if (input.firstName !== undefined) p.firstName = input.firstName;
    if (input.lastName !== undefined) p.lastName = input.lastName;
    p.displayName = input.displayName || [p.firstName, p.lastName].filter(Boolean).join(" ") || p.displayName || "Unnamed";
    if (input.headline !== undefined) p.headline = input.headline;
    if (input.bio !== undefined) p.bio = input.bio;
    if (input.pronouns !== undefined) p.pronouns = input.pronouns;
    if (input.birthday !== undefined) p.birthday = input.birthday;
    if (input.websites) p.websites = input.websites;
    if (input.interests) p.interests = input.interests;
    if (input.avatarUrl !== undefined) p.avatarUrl = input.avatarUrl;
    if (input.isPrivate !== undefined) c.isPrivate = input.isPrivate;
    if (input.fields) p.fields = { ...p.fields, ...input.fields };
    if (input.company !== undefined || input.title !== undefined) {
      const current = p.workHistory.find((w) => w.isCurrent);
      if (current) {
        if (input.company !== undefined) current.company = input.company ?? "";
        if (input.title !== undefined) current.title = input.title;
      } else if (input.company || input.title) {
        p.workHistory.unshift({ company: input.company ?? "", title: input.title ?? null, startDate: null, endDate: null, isCurrent: true, location: null });
      }
      const w = p.workHistory.find((w) => w.isCurrent);
      p.headline = input.headline ?? (w ? [w.title, w.company].filter(Boolean).join(" at ") : p.headline);
    }
    if (input.location !== undefined) {
      if (input.location) {
        if (p.addresses[0]) p.addresses[0].city = input.location;
        else p.addresses.push({ label: null, street: null, city: input.location, region: null, postalCode: null, country: null, latitude: null, longitude: null });
      } else p.addresses = [];
    }
    if (input.socialLinks) p.socialLinks = input.socialLinks.map((s) => ({ platform: s.platform as ContactProfile["socialLinks"][number]["platform"], url: s.url, handle: s.url.split("/").filter(Boolean).pop() ?? null }));
    for (const e of input.emails ?? []) this.addLookup(contactId, "email", e.value.toLowerCase(), e.label ?? null, input.source ?? "manual");
    for (const ph of input.phones ?? []) this.addLookup(contactId, "phone", ph.value, ph.label ?? null, input.source ?? "manual");
  }

  private addLookup(contactId: Id, type: Lookup["type"], value: string, label: string | null, source: ContactLookup["source"]): { contactLookupId: Id; conflicts: Id[] } {
    let lookup = this.seed.lookups.find((l) => l.type === type && l.value === value);
    if (!lookup) {
      lookup = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, type, value };
      this.seed.lookups.push(lookup);
    }
    const existing = this.seed.contactLookups.filter((cl) => cl.lookupId === lookup!.id && !cl.deleted);
    if (existing.some((cl) => cl.contactId === contactId)) return { contactLookupId: existing.find((cl) => cl.contactId === contactId)!.id, conflicts: [] };
    const conflicts = existing.map((cl) => cl.contactId);
    const now = nowIso();
    const cl: ContactLookup = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, lookupId: lookup.id, contactId, source, isPrimary: !this.lookupsFor(contactId).some((l) => l.lookup.type === type), label, score: null, isInternal: false, parentId: null, userId: null };
    this.seed.contactLookups.push(cl);
    return { contactLookupId: cl.id, conflicts };
  }

  private createBlankContact(): Id {
    const id = newId();
    const now = nowIso();
    this.seed.contacts.push({ id, workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, relationship: 0, relationshipPinned: false, isLocal: false, isArchived: false, archivedAt: null, isMemorialized: false, sendBirthdayNotification: true, score: 0, originSource: "manual", linkedUserId: null, sharedByUserId: null, isPrivate: false });
    this.seed.profiles.set(id, { contactId: id, workspaceId: MOCK_WORKSPACE_ID, firstName: null, lastName: null, displayName: "Unnamed", sortFirstName: null, sortLastName: null, nickname: null, pronouns: null, headline: null,
      company: null,
      title: null,
      location: null, bio: null, birthday: null, workHistory: [], education: [], addresses: [], websites: [], socialLinks: [], interests: [], fields: {}, avatarId: null, avatarUrl: null, memorialized: null, dataUpdated: now });
    this.seed.statistics.set(id, { workspaceId: MOCK_WORKSPACE_ID, userId: null, contactId: id, emailCount: 0, eventCount: 0, messageCount: 0, callCount: 0, firstEmailDate: null, lastEmailDate: null, firstEventDate: null, lastEventDate: null, nextEventDate: null, firstMessageDate: null, lastMessageDate: null, firstCallDate: null, lastCallDate: null, firstInteractionDate: null, lastInteractionDate: null, score: 0, totalMinutes: 0, bucket: 4, interactionType: "derived" });
    return id;
  }

  private findDuplicatesFor(contactId: Id): Id[] {
    const p = this.profile(contactId);
    const lookups = this.lookupsFor(contactId).map((l) => l.lookupId);
    return this.seed.contacts
      .filter((c) => c.id !== contactId && !c.deleted)
      .filter((c) => this.profile(c.id).displayName.toLowerCase() === p.displayName.toLowerCase() || this.lookupsFor(c.id).some((l) => lookups.includes(l.lookupId)))
      .map((c) => c.id);
  }

  // ----------------------------------------------------------------- handlers

  private handlers: Handlers = {
    init: () => {
      this.initialised = true;
      setTimeout(() => this.emit({ method: "syncStatus", params: this.syncStatus }), 0);
      return { schemaVersion: 1 };
    },
    close: () => undefined,
    setToken: () => undefined,
    getSyncStatus: () => this.syncStatus,
    syncNow: async () => {
      this.syncStatus = { ...this.syncStatus, phase: "pulling" };
      this.emit({ method: "syncStatus", params: this.syncStatus });
      await new Promise((r) => setTimeout(r, 400));
      this.syncStatus = { ...this.syncStatus, phase: "idle", lastPullAt: nowIso(), lastPushAt: nowIso(), pendingOps: 0 };
      this.emit({ method: "syncStatus", params: this.syncStatus });
      return this.syncStatus;
    },
    resetLocalData: () => {
      this.seed = buildSeed();
      this.timelineState.clear();
      this.emit({ method: "contactsChanged", params: { contactIds: [] } });
    },
    exportUserData: ({ format }) => {
      const rows = this.seed.contacts.filter((c) => !c.deleted).map((c) => this.row(c.id));
      if (format === "json") return { json: JSON.stringify({ contacts: rows, notes: this.seed.notes, groups: this.seed.groups }, null, 2) };
      return {
        contactsCsv: toCsv(["Name", "Email", "Phone", "Company", "Title", "Location"], rows.map((r) => [r.displayName, r.primaryEmail, r.primaryPhone, r.company, r.headline, r.location])),
        notesCsv: toCsv(["Person", "Created", "Label", "Note"], this.seed.notes.filter((n) => !n.deleted).map((n) => [n.contactId ? this.profile(n.contactId).displayName : "", n.created, n.label, n.plainText])),
      };
    },
    getWorkspaceInteractionStats: () => {
      const ints = this.seed.interactions;
      return { contacts: this.seed.contacts.filter((c) => !c.deleted).length, interactions: ints.length, emails: ints.filter((i) => i.type === "email").length, events: ints.filter((i) => i.type === "event").length, messages: ints.filter((i) => i.type === "message_day").length, calls: ints.filter((i) => i.type === "call").length, notes: this.seed.notes.length, reminders: this.seed.reminders.length, groups: this.seed.groups.length, integrations: this.seed.integrations.length };
    },

    getCurrentUser: () => mockUser,
    setCurrentUser: () => undefined,
    listWorkspaces: () => [mockWorkspace, { ...mockWorkspace, id: "ws-mock-0002", name: "Acme team" }],
    listWorkspaceMembers: () => [
      { workspaceId: MOCK_WORKSPACE_ID, userId: MOCK_USER_ID, role: "owner", contactId: this.selfContactId, created: mockUser.created, user: mockUser },
      { workspaceId: MOCK_WORKSPACE_ID, userId: "user-mock-0002", role: "member", contactId: null, created: mockUser.created, user: { ...mockUser, id: "user-mock-0002", email: "jordan@example.com", username: "jordan", firstName: "Jordan", lastName: "Lee" } },
    ],
    getSelfContact: () => (this.selfContactId ? this.detail(this.selfContactId) : null),
    setSelfContact: ({ contactId }) => { this.selfContactId = contactId; },
    getUserSettings: () => this.userSettings,
    saveUserSettings: (patch) => { this.userSettings = { ...this.userSettings, ...patch }; return this.userSettings; },
    getUserNotificationSettings: () => this.notificationSettings,
    saveUserNotificationSettings: (patch) => { this.notificationSettings = { ...this.notificationSettings, ...patch }; return this.notificationSettings; },
    getWorkspaceSettings: () => this.workspaceSettings,
    saveWorkspaceSettings: (patch) => { this.workspaceSettings = { ...this.workspaceSettings, ...patch }; return this.workspaceSettings; },

    getContactCount: ({ includeArchived } = {}) => this.seed.contacts.filter((c) => !c.deleted && (includeArchived || !c.isArchived)).length,
    queryContacts: (q) => {
      let rows = this.searchRows(q.query, q.includeArchived);
      if (q.groupId) {
        const g = this.seed.groups.find((g) => g.id === q.groupId);
        const ids = g ? new Set(this.memberIds(g)) : new Set<Id>();
        rows = rows.filter((r) => ids.has(r.contactId));
      }
      if (q.starredOnly) rows = rows.filter((r) => r.isStarred);
      return this.page(this.sortRows(rows, q.sort, q.direction ?? (q.sort && q.sort !== "name" ? "desc" : "asc")), q.offset, q.limit);
    },
    getContactRows: ({ contactIds }) => contactIds.filter((id) => this.seed.profiles.has(id)).map((id) => this.row(id)),
    getContact: ({ contactId }) => (this.seed.profiles.has(contactId) && !this.seed.contacts.find((c) => c.id === contactId)?.deleted ? this.detail(contactId) : null),
    getContactProfile: ({ contactId }) => this.seed.profiles.get(contactId) ?? null,
    getContactProfiles: ({ contactIds }) => contactIds.map((id) => this.seed.profiles.get(id)).filter((p): p is ContactProfile => !!p),
    getContactTimeline: ({ contactId, filter, order, offset, limit }) => this.page(this.timelineFor(contactId, filter, order), offset, limit ?? 30),
    getInteractionsByContact: ({ contactId, filter, order, offset, limit }) => this.page(this.timelineFor(contactId, filter, order), offset, limit ?? 30),
    getContactTimelineFilters: ({ contactId }) => {
      const ints = this.seed.interactions.filter((i) => i.contactIds.includes(contactId));
      return { types: Array.from(new Set(ints.map((i) => i.type))), integrations: this.seed.integrations.filter((i) => ["int-google", "int-calendar", "int-apple-messages"].includes(i.id)) };
    },
    getContactHighlights: ({ contactId }) => this.highlights(contactId),
    getContactAvatarOptions: ({ contactId }) => this.avatarsFor(contactId),
    getContactFieldValues: ({ contactId }) => {
      const p = this.profile(contactId);
      const c = this.contact(contactId);
      const mk = (fieldName: string, value: string, ordinal: number, source: ContactFieldValue["source"]): ContactFieldValue => ({ id: `fv-${contactId}-${fieldName}-${ordinal}`, workspaceId: MOCK_WORKSPACE_ID, hlc: c.hlc, created: c.created, updated: c.updated, deleted: null, contactId, fieldName, value, ordinal, compositeKey: null, source });
      const out: ContactFieldValue[] = [];
      if (p.firstName) out.push(mk("first_name", p.firstName, 0, c.originSource || "manual"));
      if (p.lastName) out.push(mk("last_name", p.lastName, 0, c.originSource || "manual"));
      if (p.headline) out.push(mk("headline", p.headline, 0, "linkedin"));
      p.workHistory.forEach((w, i) => out.push(mk("work_history", JSON.stringify(w), i, i === 0 ? "linkedin" : "google")));
      p.education.forEach((e, i) => out.push(mk("education", JSON.stringify(e), i, "linkedin")));
      p.websites.forEach((w, i) => out.push(mk("website", w, i, "manual")));
      p.addresses.forEach((a, i) => out.push(mk("address", JSON.stringify(a), i, "apple_contacts")));
      Object.entries(p.fields).forEach(([k, v], i) => out.push(mk(k, Array.isArray(v) ? v.join(", ") : v, i, "manual")));
      return out;
    },
    getContactProfileChangelog: ({ contactId }) => (contactId === "c-005" ? [{ fieldName: "title", oldValue: "Director of Engineering", newValue: "VP Engineering", source: "linkedin", changedAt: new Date(this.now().getTime() - 2 * 86_400_000).toISOString() }] : []),
    findContactsByLookup: (params) => params.map((l) => ({ lookup: l, contactIds: this.seed.contactLookups.filter((cl) => !cl.deleted && this.seed.lookups.find((x) => x.id === cl.lookupId && x.type === l.type && x.value === l.value)).map((cl) => cl.contactId) })),
    findContactsByUrl: ({ url }) => {
      let domain: string | null = null;
      try { domain = new URL(url).hostname.replace(/^www\./, ""); } catch { domain = null; }
      const contacts = domain ? this.seed.contacts.filter((c) => !c.deleted && this.profile(c.id).socialLinks.some((s) => s.url.includes(url))).map((c) => this.row(c.id)) : [];
      const domainContacts = domain ? this.seed.contacts.filter((c) => !c.deleted && (this.row(c.id).primaryEmail ?? "").endsWith(`@${domain}`)).map((c) => this.row(c.id)) : [];
      return { contacts, domainContacts, domain };
    },
    getMapPins: ({ query } = {}) => this.searchRows(query).filter((r) => this.seed.coords.has(r.contactId)).map((r) => { const c = this.seed.coords.get(r.contactId)!; return { contactId: r.contactId, displayName: r.displayName, avatarUrl: r.avatarUrl, bucket: r.bucket, latitude: c.lat, longitude: c.lon, city: c.city }; }),
    getConnectionGraph: ({ contactId, limit }) => this.connectionGraph(contactId, limit ?? 24),
    searchArchivedContacts: ({ query, offset, limit }) => this.page(this.searchRows(query, true).filter((r) => r.isArchived), offset, limit),

    createContact: (input) => {
      const id = this.createBlankContact();
      this.applyInput(id, input);
      this.contact(id).originSource = input.source ?? "manual";
      this.touch(id);
      return { contactId: id, duplicates: this.findDuplicatesFor(id) };
    },
    updateContact: ({ contactId, input }) => { this.applyInput(contactId, input); this.touch(contactId); return this.profile(contactId); },
    archiveContact: ({ contactId }) => { const c = this.contact(contactId); c.isArchived = true; c.archivedAt = nowIso(); this.seed.starred.delete(contactId); this.touch(contactId); },
    restoreContact: ({ contactId }) => { const c = this.contact(contactId); c.isArchived = false; c.archivedAt = null; this.touch(contactId); },
    deleteContact: ({ contactId }) => { const c = this.contact(contactId); c.deleted = nowIso(); this.seed.starred.delete(contactId); this.emit({ method: "contactsChanged", params: { contactIds: [contactId] } }); },
    setContactRelationship: ({ contactId, relationship }) => { const c = this.contact(contactId); c.relationship = relationship ?? 0; c.relationshipPinned = relationship !== null; this.touch(contactId); },
    setContactPrivate: ({ contactId, isPrivate }) => { this.contact(contactId).isPrivate = isPrivate; this.touch(contactId); },
    setContactMemorialized: ({ contactId, memorialized }) => { this.contact(contactId).isMemorialized = memorialized; this.profile(contactId).memorialized = memorialized ? nowIso() : null; this.touch(contactId); },
    setContactStarred: ({ contactId, starred }) => { if (starred) this.seed.starred.add(contactId); else this.seed.starred.delete(contactId); this.touch(contactId); },
    setContactAvatar: ({ contactId, avatarId, url }) => { const p = this.profile(contactId); const av = this.avatarsFor(contactId).find((a) => a.id === avatarId); p.avatarUrl = url ?? av?.url ?? null; p.avatarId = avatarId; this.touch(contactId); },
    clearContactProfile: ({ contactId }) => { const p = this.profile(contactId); p.bio = null; p.workHistory = []; p.education = []; p.interests = []; p.websites = []; p.socialLinks = []; p.headline = null; p.fields = {}; this.touch(contactId); },
    setContactFieldValue: ({ contactId, fieldName, value, ordinal }) => { const p = this.profile(contactId); p.fields[fieldName] = value; this.touch(contactId); const now = nowIso(); return { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId, fieldName, value, ordinal: ordinal ?? 0, compositeKey: null, source: "manual" }; },
    clearContactFieldValue: () => undefined,
    setProfileOverride: () => undefined,
    clearProfileOverride: () => undefined,
    defineCustomField: (def) => { const row = { ...def, workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(nowIso()) }; this.customFields = [...this.customFields.filter((f) => f.fieldName !== def.fieldName), row]; return row; },
    getCustomFieldDefinitions: () => this.customFields,
    getContactSources: () => { const counts = new Map<string, number>(); for (const c of this.seed.contacts) if (!c.deleted && c.originSource) counts.set(c.originSource, (counts.get(c.originSource) ?? 0) + 1); return [...counts].sort(([a], [b]) => a.localeCompare(b)).map(([source, count]) => ({ source, count })); },

    getContactLookups: ({ contactId }) => this.lookupsFor(contactId),
    addContactLookup: ({ contactId, type, value, label, source }) => { const r = this.addLookup(contactId, type, value, label ?? null, source ?? "manual"); this.touch(contactId); return r; },
    removeContactLookup: ({ contactLookupId }) => { const cl = this.seed.contactLookups.find((c) => c.id === contactLookupId); if (cl) { cl.deleted = nowIso(); this.touch(cl.contactId); } },
    moveContactLookup: ({ contactLookupId, toContactId }) => { const cl = this.seed.contactLookups.find((c) => c.id === contactLookupId); if (cl) { const from = cl.contactId; cl.contactId = toContactId; this.touch(from); this.touch(toContactId); } },
    moveContactLookupToNewContact: ({ contactLookupId }) => { const cl = this.seed.contactLookups.find((c) => c.id === contactLookupId); const id = this.createBlankContact(); if (cl) { const from = cl.contactId; cl.contactId = id; const lk = this.seed.lookups.find((l) => l.id === cl.lookupId); if (lk) this.profile(id).displayName = lk.value; this.touch(from); } this.touch(id); return { contactId: id }; },
    setContactLookupLabel: ({ contactLookupId, label }) => { const cl = this.seed.contactLookups.find((c) => c.id === contactLookupId); if (cl) { cl.label = label; this.touch(cl.contactId); } },
    setContactLookupPrimary: ({ contactLookupId }) => { const cl = this.seed.contactLookups.find((c) => c.id === contactLookupId); if (!cl) return; const type = this.seed.lookups.find((l) => l.id === cl.lookupId)?.type; for (const other of this.seed.contactLookups.filter((o) => o.contactId === cl.contactId && this.seed.lookups.find((l) => l.id === o.lookupId)?.type === type)) other.isPrimary = false; cl.isPrimary = true; this.touch(cl.contactId); },

    getRelatedContacts: ({ contactId }) => this.seed.related.filter((r) => r.contactId === contactId && !r.deleted).map((r) => ({ ...r, labels: this.seed.relatedLabels.filter((l) => l.contactId === contactId && l.relatedContactId === r.relatedContactId && !l.deleted).map((l) => l.label) })),
    addRelatedContact: ({ contactId, relatedContactId, label }) => {
      const now = nowIso();
      for (const [a, b] of [[contactId, relatedContactId], [relatedContactId, contactId]]) {
        if (!this.seed.related.some((r) => r.contactId === a && r.relatedContactId === b && !r.deleted)) this.seed.related.push({ id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId: a, relatedContactId: b });
      }
      if (label) this.seed.relatedLabels.push({ id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId, relatedContactId, label });
      this.touch(contactId);
    },
    removeRelatedContact: ({ contactId, relatedContactId }) => { for (const r of this.seed.related) if ((r.contactId === contactId && r.relatedContactId === relatedContactId) || (r.contactId === relatedContactId && r.relatedContactId === contactId)) r.deleted = nowIso(); this.touch(contactId); },
    setRelatedContactLabel: ({ contactId, relatedContactId, label }) => { const now = nowIso(); this.seed.relatedLabels.push({ id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId, relatedContactId, label }); this.touch(contactId); },
    removeRelatedContactLabel: ({ contactId, relatedContactId, label }) => { for (const l of this.seed.relatedLabels) if (l.contactId === contactId && l.relatedContactId === relatedContactId && l.label === label) l.deleted = nowIso(); this.touch(contactId); },
    getRelatedContactLabels: () => ["spouse", "partner", "child", "parent", "sibling", "colleague", "manager", "report", "friend", "introduced-by"],

    getMilestones: ({ contactId } = {}) => this.seed.milestones.filter((m) => !m.deleted && (!contactId || m.contactId === contactId)),
    saveMilestone: (m) => {
      const now = nowIso();
      const existing = m.id ? this.seed.milestones.find((x) => x.id === m.id) : undefined;
      const row: MilestoneDate = { ...(existing ?? { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, year: null, description: null, notify: true, system: false, nextOccurrence: null, lastOccurrence: null }), ...m, updated: now, nextOccurrence: nextOccurrence(m.month, m.day, this.now()) } as MilestoneDate;
      if (!existing) this.seed.milestones.push(row);
      else Object.assign(existing, row);
      if (row.dateType === "birthday") this.profile(row.contactId).birthday = { year: row.year, month: row.month, day: row.day };
      this.touch(row.contactId);
      return row;
    },
    deleteMilestone: ({ milestoneId }) => { const m = this.seed.milestones.find((x) => x.id === milestoneId); if (m) { m.deleted = nowIso(); if (m.dateType === "birthday") this.profile(m.contactId).birthday = null; this.touch(m.contactId); } },
    getUpcomingBirthdays: ({ days } = {}) => this.upcomingBirthdays(days ?? 30),

    listPotentialDuplicates: () => this.duplicates.filter((d) => d.status === "pending").map((d) => ({ ...d, contacts: d.contactIds.filter((id) => !this.seed.contacts.find((c) => c.id === id)?.deleted).map((id) => this.row(id)) })),
    scanDuplicates: () => ({ found: this.duplicates.filter((d) => d.status === "pending").length }),
    mergeContacts: ({ targetContactId, sourceContactIds }) => {
      const mergeIds: Id[] = [];
      for (const sid of sourceContactIds) {
        const movedLookups = this.seed.contactLookups.filter((cl) => cl.contactId === sid).map((cl) => cl.id);
        const movedNotes = this.seed.notes.filter((n) => n.contactId === sid).map((n) => n.id);
        const movedReminders = this.seed.reminders.filter((r) => r.contactId === sid).map((r) => r.id);
        const movedGroups = this.seed.contactGroups.filter((cg) => cg.contactId === sid).map((cg) => cg.groupId);
        const movedInteractions = this.seed.interactions.filter((i) => i.contactIds.includes(sid)).map((i) => i.id);
        for (const cl of this.seed.contactLookups.filter((cl) => cl.contactId === sid)) cl.contactId = targetContactId;
        for (const n of this.seed.notes.filter((n) => n.contactId === sid)) n.contactId = targetContactId;
        for (const r of this.seed.reminders.filter((r) => r.contactId === sid)) r.contactId = targetContactId;
        for (const i of this.seed.interactions) i.contactIds = i.contactIds.map((id) => (id === sid ? targetContactId : id));
        for (const cg of this.seed.contactGroups.filter((cg) => cg.contactId === sid)) cg.contactId = targetContactId;
        this.contact(sid).deleted = nowIso();
        const mergeId = newId();
        mergeIds.push(mergeId);
        this.merges.push({ id: mergeId, workspaceId: MOCK_WORKSPACE_ID, performedByUserId: MOCK_USER_ID, sourceContactId: sid, targetContactId, mergedLookupIds: movedLookups, mergedNoteIds: movedNotes, mergedDateIds: [], mergedFieldValueIds: [], sourceSnapshot: { reminders: movedReminders, groups: movedGroups, interactions: movedInteractions }, hlc: hlc(nowIso()), created: nowIso(), undone: null });
      }
      for (const d of this.duplicates) if (d.contactIds.some((id) => sourceContactIds.includes(id) || id === targetContactId)) d.status = "resolved";
      this.touch(targetContactId);
      this.emit({ method: "contactsChanged", params: { contactIds: sourceContactIds } });
      return { mergeIds };
    },
    unmergeContact: ({ mergeId }) => {
      const m = this.merges.find((x) => x.id === mergeId && !x.undone);
      if (!m) throw new EngineRpcError("Merge not found", ENGINE_ERROR_CODES.notFound);
      const snap = (m.sourceSnapshot ?? {}) as { reminders?: Id[]; groups?: Id[]; interactions?: Id[] };
      const source = this.seed.contacts.find((c) => c.id === m.sourceContactId);
      if (source) source.deleted = null;
      for (const cl of this.seed.contactLookups) if (m.mergedLookupIds.includes(cl.id)) cl.contactId = m.sourceContactId;
      for (const n of this.seed.notes) if (m.mergedNoteIds.includes(n.id)) n.contactId = m.sourceContactId;
      for (const r of this.seed.reminders) if (snap.reminders?.includes(r.id)) r.contactId = m.sourceContactId;
      for (const i of this.seed.interactions) if (snap.interactions?.includes(i.id)) i.contactIds = i.contactIds.map((id) => (id === m.targetContactId ? m.sourceContactId : id));
      for (const gid of snap.groups ?? []) { const cg = this.seed.contactGroups.find((x) => x.groupId === gid && x.contactId === m.targetContactId); if (cg) cg.contactId = m.sourceContactId; }
      m.undone = nowIso();
      this.touch(m.targetContactId);
      this.emit({ method: "contactsChanged", params: { contactIds: [m.sourceContactId, m.targetContactId] } });
      return { restoredContactId: m.sourceContactId };
    },
    dismissPotentialDuplicate: ({ duplicateId }) => { const d = this.duplicates.find((d) => d.id === duplicateId); if (d) d.status = "dismissed"; },
    removePotentialDuplicateContact: ({ duplicateId, contactId }) => { const d = this.duplicates.find((d) => d.id === duplicateId); if (d) { d.contactIds = d.contactIds.filter((id) => id !== contactId); d.removedContactIds.push(contactId); if (d.contactIds.length < 2) d.status = "dismissed"; } },
    listMerges: ({ contactId } = {}) => this.merges.filter((m) => !contactId || m.targetContactId === contactId || m.sourceContactId === contactId),
    setDuplicateAutomergeEnabled: ({ enabled }) => { this.workspaceSettings.automergeEnabled = enabled; },

    listGroups: () => this.seed.groups.filter((g) => !g.deleted).map((g) => ({ ...g, memberCount: this.memberIds(g).length })),
    getGroup: ({ groupId }) => { const g = this.seed.groups.find((g) => g.id === groupId && !g.deleted); return g ? { ...g, memberCount: this.memberIds(g).length, notes: this.seed.notes.filter((n) => n.groupId === groupId && !n.deleted) } : null; },
    getGroupsForContact: ({ contactId }) => this.groupIdsFor(contactId).map((id) => this.seed.groups.find((g) => g.id === id)!).filter(Boolean),
    getContactsInGroup: ({ groupId, offset, limit, sort, direction, query }) => {
      const g = this.seed.groups.find((g) => g.id === groupId);
      if (!g) return this.page([], 0, 0);
      const ids = new Set(this.memberIds(g));
      const rows = this.searchRows(query, false).filter((r) => ids.has(r.contactId));
      return this.page(this.sortRows(rows, sort, direction), offset, limit ?? 100);
    },
    createGroup: ({ contactIds, ...input }) => {
      const now = nowIso();
      const g: Group = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, parentId: input.parentId ?? null, createdByUserId: MOCK_USER_ID, name: input.name, description: input.description ?? null, kind: input.kind ?? (input.filter ? "smart" : "static"), filter: input.filter ?? null, color: input.color ?? null, slug: input.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"), isInternal: false, sortOrder: String(this.seed.groups.length), reconnectCadence: input.reconnectCadence ?? null };
      this.seed.groups.push(g);
      for (const cid of contactIds ?? []) this.seed.contactGroups.push({ groupId: g.id, contactId: cid });
      this.emit({ method: "contactsChanged", params: { contactIds: contactIds ?? [] } });
      return g;
    },
    updateGroup: ({ groupId, input }) => { const g = this.seed.groups.find((g) => g.id === groupId); if (!g) throw new EngineRpcError("Group not found", ENGINE_ERROR_CODES.notFound); Object.assign(g, { ...input, updated: nowIso() }); if (input.filter !== undefined) g.kind = input.filter ? "smart" : g.kind; this.emit({ method: "contactsChanged", params: { contactIds: [] } }); return g; },
    deleteGroup: ({ groupId }) => { const g = this.seed.groups.find((g) => g.id === groupId); if (g) g.deleted = nowIso(); this.emit({ method: "contactsChanged", params: { contactIds: [] } }); },
    addContactsToGroup: ({ groupId, contactIds }) => { for (const cid of contactIds) { if (groupId === "g-fav") this.seed.starred.add(cid); else if (!this.seed.contactGroups.some((cg) => cg.groupId === groupId && cg.contactId === cid)) this.seed.contactGroups.push({ groupId, contactId: cid }); } this.emit({ method: "contactsChanged", params: { contactIds } }); },
    removeContactsFromGroup: ({ groupId, contactIds }) => { if (groupId === "g-fav") contactIds.forEach((id) => this.seed.starred.delete(id)); this.seed.contactGroups = this.seed.contactGroups.filter((cg) => !(cg.groupId === groupId && contactIds.includes(cg.contactId))); this.emit({ method: "contactsChanged", params: { contactIds } }); },
    getGroupHighlights: ({ groupId }) => {
      const g = this.seed.groups.find((g) => g.id === groupId);
      if (!g) return [];
      const ids = this.memberIds(g);
      const rows = ids.map((id) => this.row(id));
      const companies = Array.from(new Set(rows.map((r) => r.company).filter(Boolean)));
      const cities = Array.from(new Set(rows.map((r) => r.location).filter(Boolean)));
      const out: Highlight[] = [{ kind: "relationship", text: `${rows.length} people, ${rows.filter((r) => r.bucket <= 2).length} of them close or closer.` }];
      if (companies.length) out.push({ kind: "work", text: `Companies: ${companies.slice(0, 5).join(", ")}${companies.length > 5 ? ` and ${companies.length - 5} more` : ""}.` });
      if (cities.length) out.push({ kind: "work", text: `Mostly in ${cities.slice(0, 3).join(", ")}.` });
      const stale = rows.filter((r) => !r.lastInteractionDate || new Date(r.lastInteractionDate).getTime() < this.now().getTime() - 90 * 86_400_000);
      if (stale.length) out.push({ kind: "relationship", text: `${stale.length} people you have not talked to in 90 days.`, contactIds: stale.map((r) => r.contactId) });
      return out;
    },
    exportGroupCsv: ({ groupId }) => { const g = this.seed.groups.find((g) => g.id === groupId); const rows = g ? this.memberIds(g).map((id) => this.row(id)) : []; return { csv: toCsv(["Name", "Email", "Phone", "Company", "Title", "Location"], rows.map((r) => [r.displayName, r.primaryEmail, r.primaryPhone, r.company, r.headline, r.location])) }; },

    getAllNotes: ({ offset, limit, query, label }) => {
      let notes = this.seed.notes.filter((n) => !n.deleted);
      if (label) notes = notes.filter((n) => n.label === label);
      if (query) notes = notes.filter((n) => n.plainText.toLowerCase().includes(query.toLowerCase()) || (n.contactId && this.profile(n.contactId).displayName.toLowerCase().includes(query.toLowerCase())));
      notes.sort((a, b) => (a.created < b.created ? 1 : -1));
      return this.page(notes.map((n) => ({ ...n, contactName: n.contactId ? this.profile(n.contactId).displayName : null, groupName: n.groupId ? this.seed.groups.find((g) => g.id === n.groupId)?.name ?? null : null })), offset, limit ?? 50);
    },
    getContactNotes: ({ contactId }) => this.seed.notes.filter((n) => n.contactId === contactId && !n.deleted),
    getNote: ({ noteId }) => this.seed.notes.find((n) => n.id === noteId) ?? null,
    saveNote: ({ noteId, contactId, groupId, interactionId, content, plainText, label, color, icon }) => {
      const now = nowIso();
      let note = noteId ? this.seed.notes.find((n) => n.id === noteId) : undefined;
      if (!note) {
        note = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId: contactId ?? null, groupId: groupId ?? null, interactionId: interactionId ?? null, authorUserId: MOCK_USER_ID, content, plainText, label: label ?? null, color: color ?? null, icon: icon ?? null, providerId: null };
        this.seed.notes.unshift(note);
      } else {
        Object.assign(note, { content, plainText, updated: now, hlc: hlc(now), label: label === undefined ? note.label : label, color: color === undefined ? note.color : color, icon: icon === undefined ? note.icon : icon });
      }
      const detectedReminderAt = this.userSettings.reminderParsingDisabled ? null : detectDate(plainText, this.now());
      if (note.contactId) this.touch(note.contactId);
      return { note, detectedReminderAt };
    },
    deleteNote: ({ noteId }) => { const n = this.seed.notes.find((n) => n.id === noteId); if (n) { n.deleted = nowIso(); if (n.contactId) this.touch(n.contactId); } },
    updateNoteMetadata: ({ noteId, label, color, icon }) => { const n = this.seed.notes.find((n) => n.id === noteId); if (!n) throw new EngineRpcError("Note not found", ENGINE_ERROR_CODES.notFound); if (label !== undefined) n.label = label; if (color !== undefined) n.color = color; if (icon !== undefined) n.icon = icon; n.updated = nowIso(); return n; },
    getNoteLabels: () => Array.from(new Set(this.seed.notes.filter((n) => n.label && !n.deleted).map((n) => n.label!))).sort(),

    getReminders: ({ contactId, from, to, includeProcessed, offset, limit }) => {
      let rs = this.seed.reminders.filter((r) => !r.deleted && (includeProcessed || !r.isProcessed) && (!contactId || r.contactId === contactId) && (!from || r.fireAt >= from) && (!to || r.fireAt <= to));
      rs.sort((a, b) => (a.fireAt < b.fireAt ? -1 : 1));
      return this.page(rs.map((r) => ({ ...r, contact: r.contactId && this.seed.profiles.has(r.contactId) ? this.row(r.contactId) : null })), offset, limit ?? 100);
    },
    getPendingReminders: ({ before } = {}) => this.seed.reminders.filter((r) => !r.deleted && !r.isProcessed && r.fireAt <= (before ?? this.now().toISOString())).map((r) => ({ ...r, contact: r.contactId ? this.row(r.contactId) : null })),
    saveReminder: ({ reminderId, contactId, noteId, title, fireAt }) => {
      const now = nowIso();
      let r = reminderId ? this.seed.reminders.find((x) => x.id === reminderId) : undefined;
      if (!r) { r = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, userId: MOCK_USER_ID, contactId: contactId ?? null, noteId: noteId ?? null, reconnectCadenceId: null, title: title ?? null, fireAt, isProcessed: false, processedAt: null }; this.seed.reminders.push(r); }
      else Object.assign(r, { title: title ?? r.title, fireAt, contactId: contactId === undefined ? r.contactId : contactId, updated: now });
      if (r.contactId) this.touch(r.contactId);
      return r;
    },
    deleteReminder: ({ reminderId }) => { const r = this.seed.reminders.find((x) => x.id === reminderId); if (r) { r.deleted = nowIso(); if (r.contactId) this.touch(r.contactId); } },
    markReminderProcessed: ({ reminderId, processed = true }) => { const r = this.seed.reminders.find((x) => x.id === reminderId); if (!r) throw new EngineRpcError("Reminder not found", ENGINE_ERROR_CODES.notFound); r.isProcessed = processed; r.processedAt = processed ? nowIso() : null; if (r.contactId) this.touch(r.contactId); this.emit({ method: "timelineChanged", params: { unread: 0 } }); return r; },
    getReconnectCadence: ({ contactId }) => this.cadenceState(contactId),
    getResolvedReconnectCadence: ({ contactId }) => {
      const st = this.cadenceState(contactId);
      const row = this.row(contactId);
      const groupCadence = this.groupIdsFor(contactId).map((gid) => this.seed.groups.find((g) => g.id === gid)?.reconnectCadence).find(Boolean) ?? null;
      const name = st.state === "disabled" ? "disabled" : st.state === "auto" ? groupCadence ?? automaticCadenceForScore(row.score) : st.state;
      const nextDue = name === "disabled" ? null : new Date(new Date(row.lastInteractionDate ?? this.now().toISOString()).getTime() + CADENCE_DAYS[name] * 86_400_000).toISOString();
      return { state: name, nextDue, source: st.state === "auto" ? (groupCadence ? "group" : "auto") : "contact" };
    },
    saveReconnectCadence: (input) => {
      const now = nowIso();
      let row = this.seed.cadences.find((c) => c.contactId === input.contactId && !c.deleted);
      if (!row) { row = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId: input.contactId, cadence: null, qualifier: null, weekdayNumber: null, monthNumber: null, dayNumber: null, isDisabled: false, isAutomatic: false, isCustom: null }; this.seed.cadences.push(row); }
      Object.assign(row, { cadence: input.cadence, qualifier: input.qualifier ?? null, weekdayNumber: input.weekdayNumber ?? null, monthNumber: input.monthNumber ?? null, dayNumber: input.dayNumber ?? null, isDisabled: !!input.isDisabled, isAutomatic: !!input.isAutomatic, isCustom: !!input.qualifier, updated: now } satisfies Partial<ReconnectCadence>);
      this.touch(input.contactId);
      return row;
    },
    deleteReconnectCadence: ({ contactId }) => { for (const c of this.seed.cadences) if (c.contactId === contactId) c.deleted = nowIso(); this.touch(contactId); },
    getReconnectSuggestions: () => { const s = this.reconnectSuggestions(); return { ...s, picked: s.members.length, contacts: s.members.map((m) => this.row(m.contactId)) }; },
    getEnrichSuggestions: (p) => {
      const count = p?.count ?? 2;
      const rows = this.seed.contacts.filter((c) => !c.deleted && !c.isArchived).map((c) => this.row(c.id)).filter((r) => !r.hasNeeds && !r.hasServices).sort((a, b) => b.score - a.score).slice(0, count);
      return { localDate: this.now().toISOString().slice(0, 10), picked: rows.length, contacts: rows };
    },
    snoozeReconnect: ({ contactId, until }) => { this.snoozedReconnects.set(contactId, until); this.emit({ method: "timelineChanged", params: { unread: 0 } }); },

    getInteraction: ({ interactionId }) => { const i = this.seed.interactions.find((i) => i.id === interactionId); return i ? this.entry(i) : null; },
    upsertInteractions: ({ interactions }) => {
      let created = 0;
      for (const inp of interactions) {
        const ids = inp.participants.map((p) => this.seed.contactLookups.find((cl) => this.seed.lookups.find((l) => l.id === cl.lookupId && l.type === p.lookupType && l.value === p.value))?.contactId).filter((x): x is Id => !!x);
        this.seed.interactions.push({ id: newId(), type: inp.type, date: inp.date, contactIds: ids, subject: inp.email?.subject ?? undefined, title: inp.event?.title ?? undefined, startDatetime: inp.event?.startDatetime, endDatetime: inp.event?.endDatetime ?? undefined });
        created++;
      }
      return { created, updated: 0 };
    },
    setInteractionState: () => undefined,
    getUpcomingEvents: ({ days } = {}) => {
      const until = new Date(this.now().getTime() + (days ?? 7) * 86_400_000).toISOString();
      return this.seed.interactions.filter((i) => i.type === "event" && i.date >= this.now().toISOString() && i.date <= until).sort((a, b) => (a.date < b.date ? -1 : 1)).map((i) => ({ ...this.entry(i), contacts: i.contactIds.map((id) => this.row(id)) }));
    },
    getContactStatistics: ({ contactId }) => this.seed.statistics.get(contactId) ?? null,
    refreshStatistics: () => ({ contacts: this.seed.contacts.length }),

    parseSearch: ({ query }) => parse(query),
    getWorkspaceFacets: (p) => {
      const limit = p?.limit ?? 5;
      const top = (pick: (r: ContactRow) => string | null) => {
        const counts = new Map<string, number>();
        for (const r of this.searchRows(undefined)) { const v = pick(r)?.trim(); if (v) counts.set(v, (counts.get(v) ?? 0) + 1); }
        return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, limit).map(([v]) => v);
      };
      return {
        companies: top((r) => r.company),
        locations: top((r) => r.location),
        groups: this.seed.groups.filter((g) => !g.deleted && !g.isInternal).slice(0, limit).map((g) => g.name),
        needs: this.seed.needs.filter((n) => !n.deleted && !n.isCompleted).slice(0, limit).map((n) => n.title),
        offers: this.seed.services.filter((s) => !s.deleted && s.isActive).slice(0, limit).map((s) => s.title),
      };
    },
    search: ({ query, limit, sort, direction }) => {
      const parsed = parse(query);
      const rows = this.sortRows(this.searchRows(query), sort ?? "score", direction ?? (sort === "name" || sort === "next_event" ? "asc" : "desc")).slice(0, limit ?? 50);
      const q = parsed.freeText.toLowerCase();
      const groups = q ? this.seed.groups.filter((g) => !g.deleted && g.name.toLowerCase().includes(q)) : [];
      const notes = q ? this.seed.notes.filter((n) => !n.deleted && n.plainText.toLowerCase().includes(q)).map((n) => ({ ...n, contactName: n.contactId ? this.profile(n.contactId).displayName : null })) : [];
      const needs = q ? this.seed.needs.filter((n) => !n.deleted && (n.title + " " + n.description + " " + n.keywords.join(" ")).toLowerCase().includes(q)).map((n) => ({ ...n, contactName: this.profile(n.contactId).displayName })) : [];
      const services = q ? this.seed.services.filter((s) => !s.deleted && (s.title + " " + s.shortDescription + " " + s.keywords.join(" ")).toLowerCase().includes(q)).map((s) => ({ ...s, contactName: this.profile(s.contactId).displayName })) : [];
      return { contacts: rows, groups, notes, needs, services, parsed, total: rows.length + groups.length + notes.length + needs.length + services.length };
    },
    getRecentSearches: () => this.recentSearches,
    saveRecentSearch: ({ query }) => { this.recentSearches = [query, ...this.recentSearches.filter((q) => q !== query)].slice(0, 10); },

    getTimeline: ({ offset, limit, types, includeDismissed }) => {
      let items = this.timelineItems();
      if (types?.length) items = items.filter((i) => types.includes(i.itemType));
      if (!includeDismissed) items = items.filter((i) => i.status === "active" || (i.status === "snoozed" && i.snoozedUntil && i.snoozedUntil <= this.now().toISOString()));
      return this.page(items, offset, limit ?? 50);
    },
    getTimelineUpcoming: ({ days } = {}) => this.timelineItems().filter((i) => i.itemDate > this.now().toISOString() && i.itemDate <= new Date(this.now().getTime() + (days ?? 7) * 86_400_000).toISOString()),
    getTimelineUnreadCount: () => this.timelineItems().filter((i) => !i.seen && i.status === "active").length,
    setTimelineItemState: ({ itemKey, seen, status, snoozedUntil }) => {
      const st = this.timelineState.get(itemKey) ?? { seen: false, status: "active" as const, snoozedUntil: null, dismissedContacts: new Set<Id>() };
      if (seen !== undefined) st.seen = seen;
      if (status !== undefined) st.status = status;
      if (snoozedUntil !== undefined) st.snoozedUntil = snoozedUntil;
      this.timelineState.set(itemKey, st);
      this.emit({ method: "timelineChanged", params: { unread: this.timelineItems().filter((i) => !i.seen).length } });
    },
    setTimelineItemsSeen: ({ itemKeys }) => { for (const k of itemKeys) { const st = this.timelineState.get(k) ?? { seen: false, status: "active" as const, snoozedUntil: null, dismissedContacts: new Set<Id>() }; st.seen = true; this.timelineState.set(k, st); } this.emit({ method: "timelineChanged", params: { unread: this.timelineItems().filter((i) => !i.seen).length } }); },
    setTimelineItemContactState: ({ itemKey, contactId, status }) => { const st = this.timelineState.get(itemKey) ?? { seen: false, status: "active" as const, snoozedUntil: null, dismissedContacts: new Set<Id>() }; if (status === "dismissed") st.dismissedContacts.add(contactId); else st.dismissedContacts.delete(contactId); this.timelineState.set(itemKey, st); this.emit({ method: "timelineChanged", params: { unread: 0 } }); },
    refreshTimeline: () => ({ items: this.timelineItems().length }),
    pruneStaleInteractions: () => ({ removed: 0 }),
    listGeocodeCandidates: () => [],
    applyGeocodes: () => ({ stored: 0, located: 0 }),
    getGeocodeStatus: () => ({ places: 0, located: 0, unresolved: 0, pending: 0 }),
    listLocalWorkspaces: () => [],
    adoptWorkspace: () => ({ contacts: 0, rows: 0, queued: 0 }),
    scanNews: (params) => {
      const force = Boolean(params && "force" in params && params.force);
      if (!force && this.newsScannedAt && this.now().getTime() - new Date(this.newsScannedAt).getTime() < 6 * 60 * 60 * 1000) return { companies: 0, articles: 0, skipped: 3 };
      this.newsScannedAt = nowIso();
      return { companies: 3, articles: 0, skipped: 0 };
    },
    enrichAvatars: (params) => {
      const limit = params && "limit" in params && typeof params.limit === "number" ? params.limit : 50;
      const without = this.seed.contacts.filter((c) => !c.deleted && !this.profile(c.id).avatarUrl).slice(0, limit);
      return { checked: without.length, found: 0 };
    },

    beginContactImport: ({ source }) => ({ importId: `imp-${source}-${newId()}` }),
    addImportContacts: ({ importId, contacts }) => { let created = 0; for (const c of contacts) { const id = this.createBlankContact(); this.applyInput(id, c); this.contact(id).originSource = c.source ?? "csv"; created++; } this.emit({ method: "contactsChanged", params: { contactIds: [] } }); return { importId, source: "csv", phase: "writing", processed: contacts.length, total: contacts.length, created, updated: 0, message: null }; },
    finishContactImport: ({ importId }) => ({ importId, source: "csv", phase: "done", processed: 0, total: 0, created: 0, updated: 0, message: null }),
    abortContactImport: () => undefined,
    previewCsv: ({ csv }) => {
      const lines = parseCsv(csv);
      const headers = lines[0] ?? [];
      const rows = lines.slice(1, 6);
      const suggestedMapping: Record<string, string> = {};
      for (const h of headers) {
        const k = h.toLowerCase();
        if (/first/.test(k)) suggestedMapping[h] = "firstName";
        else if (/last|surname/.test(k)) suggestedMapping[h] = "lastName";
        else if (/^name$|full/.test(k)) suggestedMapping[h] = "displayName";
        else if (/e-?mail/.test(k)) suggestedMapping[h] = "email";
        else if (/phone|mobile/.test(k)) suggestedMapping[h] = "phone";
        else if (/company|org/.test(k)) suggestedMapping[h] = "company";
        else if (/title|position|role/.test(k)) suggestedMapping[h] = "title";
        else if (/city|location|address/.test(k)) suggestedMapping[h] = "location";
        else if (/linkedin|url|profile/.test(k)) suggestedMapping[h] = "linkedin";
        else if (/note/.test(k)) suggestedMapping[h] = "notes";
        else if (/birth/.test(k)) suggestedMapping[h] = "birthday";
        else suggestedMapping[h] = "skip";
      }
      return { headers, rows, suggestedMapping };
    },
    importCsv: ({ csv, mapping, groupName }) => {
      const lines = parseCsv(csv);
      const headers = lines[0] ?? [];
      let created = 0;
      const ids: Id[] = [];
      for (const line of lines.slice(1)) {
        if (line.every((c) => !c.trim())) continue;
        const get = (field: string) => { const idx = headers.findIndex((h) => mapping[h] === field); return idx >= 0 ? line[idx]?.trim() || null : null; };
        const id = this.createBlankContact();
        const linkedin = get("linkedin");
        this.applyInput(id, { firstName: get("firstName"), lastName: get("lastName"), displayName: get("displayName") ?? undefined, company: get("company"), title: get("title"), location: get("location"), emails: get("email") ? [{ value: get("email")! }] : [], phones: get("phone") ? [{ value: get("phone")! }] : [], socialLinks: linkedin ? [{ platform: "linkedin", url: linkedin }] : [], source: "csv" });
        this.contact(id).originSource = "csv";
        const notes = get("notes");
        if (notes) this.seed.notes.push({ id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(nowIso()), created: nowIso(), updated: nowIso(), deleted: null, contactId: id, groupId: null, interactionId: null, authorUserId: MOCK_USER_ID, content: notes, plainText: notes, label: null, color: null, icon: null, providerId: null });
        ids.push(id);
        created++;
      }
      if (groupName && ids.length) {
        const g = this.seed.groups.find((g) => g.name.toLowerCase() === groupName.toLowerCase()) ?? (this.handlers.createGroup!({ name: groupName }) as Group);
        for (const id of ids) this.seed.contactGroups.push({ groupId: g.id, contactId: id });
      }
      this.emit({ method: "contactsChanged", params: { contactIds: ids } });
      return { importId: newId(), source: "csv", phase: "done", processed: created, total: created, created, updated: 0, message: null };
    },
    getIntegrations: () => this.seed.integrations.filter((i) => !i.archived),
    getIntegrationConnectionCounts: () => this.seed.integrations.map((i) => ({ integrationId: i.id, contacts: i.capability === "contacts" ? 38 : 24, interactions: i.capability === "email" ? this.seed.interactions.filter((x) => x.type === "email").length : i.capability === "calendar" ? this.seed.interactions.filter((x) => x.type === "event").length : i.capability === "messages" ? this.seed.interactions.filter((x) => x.type === "message_day").length : 0 })),
    upsertIntegration: (input) => { const now = nowIso(); const existing = input.id ? this.seed.integrations.find((i) => i.id === input.id) : undefined; const row = { ...(existing ?? { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, userId: MOCK_USER_ID, remoteId: null, name: null, status: "active" as const, isSyncing: false, firstSync: null, lastSync: null, lastError: null, archived: false, metadata: {} }), ...input, updated: now }; if (!existing) this.seed.integrations.push(row); else Object.assign(existing, row); return row; },
    archiveIntegration: ({ integrationId }) => { const i = this.seed.integrations.find((i) => i.id === integrationId); if (i) i.archived = true; },
    deleteIntegration: ({ integrationId }) => { this.seed.integrations = this.seed.integrations.filter((i) => i.id !== integrationId); },

    listNeeds: ({ contactId, includeCompleted } = {}) => this.seed.needs.filter((n) => !n.deleted && (!contactId || n.contactId === contactId) && (includeCompleted || !n.isCompleted)),
    saveNeed: (input) => { const now = nowIso(); let n = input.id ? this.seed.needs.find((x) => x.id === input.id) : undefined; if (!n) { n = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId: input.contactId, title: input.title, description: input.description ?? "", keywords: input.keywords ?? [], keywordsGeneratedByAi: false, category: input.category ?? "General", timeline: input.timeline ?? null, location: input.location ?? null, isCompleted: false, completedAt: null, visibility: input.visibility ?? "connections" }; this.seed.needs.push(n); } else Object.assign(n, { ...input, updated: now }); this.touch(n.contactId); return n as Need; },
    completeNeed: ({ needId }) => { const n = this.seed.needs.find((x) => x.id === needId); if (!n) throw new EngineRpcError("Need not found", ENGINE_ERROR_CODES.notFound); n.isCompleted = true; n.completedAt = nowIso(); this.touch(n.contactId); return n; },
    deleteNeed: ({ needId }) => { const n = this.seed.needs.find((x) => x.id === needId); if (n) { n.deleted = nowIso(); this.touch(n.contactId); } },
    listServices: ({ contactId, includeInactive } = {}) => this.seed.services.filter((s) => !s.deleted && (!contactId || s.contactId === contactId) && (includeInactive || s.isActive)),
    saveService: (input) => { const now = nowIso(); let s = input.id ? this.seed.services.find((x) => x.id === input.id) : undefined; if (!s) { s = { id: newId(), workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(now), created: now, updated: now, deleted: null, contactId: input.contactId, title: input.title, shortDescription: input.shortDescription ?? "", longDescription: input.longDescription ?? null, keywords: input.keywords ?? [], keywordsGeneratedByAi: false, category: input.category ?? "Uncategorized", location: input.location ?? null, isActive: true, visibility: input.visibility ?? "connections" }; this.seed.services.push(s); } else Object.assign(s, { ...input, updated: now }); this.touch(s.contactId); return s as Service; },
    deleteService: ({ serviceId }) => { const s = this.seed.services.find((x) => x.id === serviceId); if (s) { s.deleted = nowIso(); this.touch(s.contactId); } },
    getMatchCards: ({ section } = {}) => this.matchCards.filter((c) => !section || c.section === section),
    getMatchCard: ({ matchId }) => this.matchCards.find((c) => c.matchId === matchId) ?? null,
    refreshMatches: () => ({ matches: this.matchCards.length }),
    cacheMatches: ({ cards }) => { this.matchCards = cards; },
    getLocalMatches: () => [],

    getPendingOps: () => [],
    applyRemoteOps: ({ ops }) => ({ applied: ops.length, cursor: ops.length ? ops[ops.length - 1].seq : 0 }),
    ackPushedOps: () => undefined,
    getSyncCursor: () => 0,
  };

  private timelineFor(contactId: Id, filter?: EngineMethods["getContactTimeline"]["params"]["filter"], order?: "newest" | "oldest"): TimelineEntry[] {
    return this.seed.interactions
      .filter((i) => i.contactIds.includes(contactId))
      .filter((i) => !filter?.types?.length || filter.types.includes(i.type))
      .filter((i) => !filter?.from || i.date >= filter.from)
      .filter((i) => !filter?.to || i.date <= filter.to)
      .map((i) => this.entry(i))
      .filter((e) => !filter?.integrationIds?.length || (e.interaction.integrationId && filter.integrationIds.includes(e.interaction.integrationId)))
      .sort((a, b) => (a.interaction.date < b.interaction.date ? 1 : -1) * (order === "oldest" ? -1 : 1));
  }

  /** Whether `init` has been called; the UI uses this only in tests. */
  get isInitialised() {
    return this.initialised;
  }
}

export function createMockEngine(opts?: { now?: () => Date }): EngineTransport {
  return new MockEngine(opts);
}

// ---------------------------------------------------------------- search grammar (subset)

const KEYWORD_SET = new Set<string>(SEARCH_KEYWORDS);

export function parse(query: string): ParsedSearch {
  const tokens = tokenize(query.trim());
  const chips: string[] = [];
  const warnings: string[] = [];
  const free: string[] = [];
  let pos = 0;

  const parseGroup = (close: string | null): SearchNode => {
    const children: SearchNode[] = [];
    while (pos < tokens.length) {
      const t = tokens[pos];
      if (close && t === close) { pos++; break; }
      pos++;
      if (t === "(") { children.push(parseGroup(")")); continue; }
      if (t === "[") { const inner = parseGroup("]"); children.push(inner.kind === "and" ? { kind: "or", children: inner.children } : inner); continue; }
      if (t === "all" || t === "*") { children.push({ kind: "all" }); continue; }
      const neg = t.startsWith("-") || t.startsWith("!");
      const body = neg ? t.slice(1) : t;
      const m = /^([a-z_.]+)(:|>=|<=|>|<|=)(.*)$/i.exec(body);
      if (m) {
        const rawKey = m[1].toLowerCase();
        const op = m[2];
        const value = stripQuotes(m[3]);
        if (rawKey === "has" || rawKey === "missing") {
          const node: SearchNode = { kind: "existence", target: value as SearchNode extends { target: infer T } ? T : never, negate: rawKey === "missing" };
          chips.push(`${rawKey === "has" ? "Has" : "Missing"} ${value}`);
          children.push(neg ? { kind: "not", child: node } : node);
          continue;
        }
        const keyword = (SEARCH_KEYWORD_ALIASES[rawKey] ?? rawKey) as SearchNode extends { keyword: infer K } ? K : never;
        if (!KEYWORD_SET.has(keyword)) { warnings.push(`Unknown keyword "${rawKey}"`); free.push(body); children.push({ kind: "text", value: body }); continue; }
        const operator = op === ":" || op === "=" ? (isDateKeyword(keyword) || isCountKeyword(keyword) ? "eq" : "contains") : op === ">=" ? "gte" : op === ">" ? "gt" : op === "<=" ? "lte" : "lt";
        const node: SearchNode = { kind: "keyword", keyword, operator, value };
        const isDate = isDateKeyword(keyword);
        const shown = isDate ? dateValueWords(value) : value;
        if (keyword === "relationship" && /^(none|acquaintance|close|closer|closest|[0-4])$/i.test(value)) { chips.push(/^\d$/.test(value) ? ["None", "Acquaintance", "Close", "Closer", "Closest"][Number(value)] : value[0].toUpperCase() + value.slice(1).toLowerCase()); children.push(neg ? { kind: "not", child: node } : node); continue; }
        if (isDate && operator === "eq" && /^(this|next|last|today|tomorrow|yesterday)/.test(shown)) chips.push(`${SEARCH_KEYWORD_LABELS[keyword] ?? capitalize(keyword)} ${shown}`);
        else chips.push(`${SEARCH_KEYWORD_LABELS[keyword] ?? capitalize(keyword)} ${operator === "gte" ? (isDate ? "on or after" : "at least") : operator === "gt" ? (isDate ? "after" : "more than") : operator === "lte" ? (isDate ? "on or before" : "at most") : operator === "lt" ? (isDate ? "before" : "fewer than") : operator === "eq" ? (isDate ? "in" : "is") : "contains"} ${shown}`);
        children.push(neg ? { kind: "not", child: node } : node);
        continue;
      }
      const phrase = PHRASES.find((p) => p.words.every((w) => tokens.slice(pos - 1, pos - 1 + p.words.length).join(" ").toLowerCase() === p.words.join(" ")));
      if (phrase) { pos += phrase.words.length - 1; chips.push(phrase.chip); children.push(phrase.node); continue; }
      const word = stripQuotes(body);
      if (STOP_WORDS.has(word.toLowerCase())) continue;
      free.push(word);
      children.push(neg ? { kind: "not", child: { kind: "text", value: word } } : { kind: "text", value: word });
    }
    return children.length === 1 ? children[0] : { kind: "and", children };
  };

  const ast = tokens.length === 0 ? ({ kind: "all" } as SearchNode) : parseGroup(null);
  return { ast, freeText: free.join(" "), chips, warnings };
}

const STOP_WORDS = new Set(["the", "a", "an", "and", "who", "people", "person", "i", "that", "with", "in", "at", "of"]);
const PHRASES: { words: string[]; chip: string; node: SearchNode }[] = [
  { words: ["closest", "friends"], chip: "Tier is closest", node: { kind: "keyword", keyword: "relationship", operator: "eq", value: "closest" } },
  { words: ["bay", "area"], chip: "Location is San Francisco", node: { kind: "keyword", keyword: "location", operator: "contains", value: "san francisco" } },
  { words: ["emailed", "last", "month"], chip: "Last email after last month", node: { kind: "keyword", keyword: "lastemail", operator: "gte", value: "lastmonth" } },
  { words: ["never", "met"], chip: "No meetings", node: { kind: "keyword", keyword: "meetingcount", operator: "eq", value: "0" } },
  { words: ["c", "suite"], chip: "Title contains chief", node: { kind: "or", children: [{ kind: "keyword", keyword: "title", operator: "contains", value: "chief" }, { kind: "keyword", keyword: "title", operator: "contains", value: "ceo" }, { kind: "keyword", keyword: "title", operator: "contains", value: "cto" }, { kind: "keyword", keyword: "title", operator: "contains", value: "coo" }, { kind: "keyword", keyword: "title", operator: "contains", value: "cfo" }] } },
];

function tokenize(q: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < q.length) {
    const ch = q[i];
    if (/\s/.test(ch)) { i++; continue; }
    if ("()[]".includes(ch)) { out.push(ch); i++; continue; }
    let tok = "";
    let inQuote = false;
    while (i < q.length) {
      const c = q[i];
      if (c === '"') { inQuote = !inQuote; tok += c; i++; continue; }
      if (!inQuote && (/\s/.test(c) || "()[]".includes(c))) break;
      tok += c;
      i++;
    }
    out.push(tok);
  }
  return out;
}

function stripQuotes(s: string) {
  return s.replace(/^"(.*)"$/, "$1");
}
function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function isDateKeyword(k: string) {
  return /met|event|email|text|meeting|created|updated|notedate|lastnote|birthday|lastcontact/.test(k);
}
function isCountKeyword(k: string) {
  return /count/.test(k);
}

/** A date token as a [start, end) range in UTC, the way the engine resolves it (see packages/engine/src/search/dates.ts). */
function resolveDate(v: string, now: Date): { start: string; end: string } | null {
  const day = (d: Date) => ({ start: d.toISOString(), end: new Date(d.getTime() + 86_400_000).toISOString() });
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const rel = /^([+-]?)(\d+)(d|w|m|y)$/.exec(v);
  if (rel) {
    const n = Number(rel[2]) * (rel[1] === "+" ? -1 : 1);
    const unit = rel[3];
    const days = unit === "d" ? n : unit === "w" ? n * 7 : unit === "m" ? n * 30 : n * 365;
    return day(new Date(today.getTime() - days * 86_400_000));
  }
  const weekStart = new Date(today.getTime() - ((today.getUTCDay() + 6) % 7) * 86_400_000);
  const monthStart = (offset: number) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  const quarterStart = (offset: number) => new Date(Date.UTC(now.getUTCFullYear(), Math.floor(now.getUTCMonth() / 3) * 3 + offset * 3, 1));
  const yearStart = (offset: number) => new Date(Date.UTC(now.getUTCFullYear() + offset, 0, 1));
  const span = (a: Date, b: Date) => ({ start: a.toISOString(), end: b.toISOString() });
  switch (v) {
    case "today": return day(today);
    case "yesterday": return day(new Date(today.getTime() - 86_400_000));
    case "tomorrow": return day(new Date(today.getTime() + 86_400_000));
    case "thisweek": return span(weekStart, new Date(weekStart.getTime() + 7 * 86_400_000));
    case "lastweek": return span(new Date(weekStart.getTime() - 7 * 86_400_000), weekStart);
    case "nextweek": return span(new Date(weekStart.getTime() + 7 * 86_400_000), new Date(weekStart.getTime() + 14 * 86_400_000));
    case "thismonth": return span(monthStart(0), monthStart(1));
    case "lastmonth": return span(monthStart(-1), monthStart(0));
    case "nextmonth": return span(monthStart(1), monthStart(2));
    case "thisquarter": return span(quarterStart(0), quarterStart(1));
    case "lastquarter": return span(quarterStart(-1), quarterStart(0));
    case "thisyear": return span(yearStart(0), yearStart(1));
    case "lastyear": return span(yearStart(-1), yearStart(0));
  }
  const iso = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(v);
  if (iso) {
    const y = Number(iso[1]);
    if (iso[3]) return day(new Date(Date.UTC(y, Number(iso[2]) - 1, Number(iso[3]))));
    if (iso[2]) return span(new Date(Date.UTC(y, Number(iso[2]) - 1, 1)), new Date(Date.UTC(y, Number(iso[2]), 1)));
    return span(new Date(Date.UTC(y, 0, 1)), new Date(Date.UTC(y + 1, 0, 1)));
  }
  const parsed = new Date(v);
  return Number.isNaN(parsed.getTime()) ? null : day(parsed);
}

/** Finds a date mention in note text ("next Tuesday", "on Nov 3", "tomorrow", "in 2 weeks"). */
export function detectDate(text: string, now: Date): string | null {
  const t = text.toLowerCase();
  const at9 = (d: Date) => { d.setHours(9, 0, 0, 0); return d.toISOString(); };
  if (/\btomorrow\b/.test(t)) return at9(new Date(now.getTime() + 86_400_000));
  const inN = /\bin (\d+) (day|week|month)s?\b/.exec(t);
  if (inN) { const n = Number(inN[1]); const days = inN[2] === "day" ? n : inN[2] === "week" ? n * 7 : n * 30; return at9(new Date(now.getTime() + days * 86_400_000)); }
  const weekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const wd = /\b(?:next |on )?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/.exec(t);
  if (wd) { const target = weekdays.indexOf(wd[1]); let delta = (target - now.getDay() + 7) % 7; if (delta === 0) delta = 7; return at9(new Date(now.getTime() + delta * 86_400_000)); }
  const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const md = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.? (\d{1,2})\b/.exec(t);
  if (md) { const m = months.indexOf(md[1]); let d = new Date(now.getFullYear(), m, Number(md[2])); if (d.getTime() < now.getTime()) d = new Date(now.getFullYear() + 1, m, Number(md[2])); return at9(d); }
  return null;
}

export function parseCsv(csv: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuote = false;
  for (let i = 0; i < csv.length; i++) {
    const c = csv[i];
    if (inQuote) {
      if (c === '"' && csv[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') inQuote = false;
      else cell += c;
    } else if (c === '"') inQuote = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && csv[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim()));
}

function clone<T>(v: T): T {
  return v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T);
}

function relative(iso: string | null, now: Date): string {
  if (!iso) return "never";
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

function relativeDay(iso: string, now: Date): string {
  const days = Math.round((new Date(iso).getTime() - new Date(now.toISOString().slice(0, 10)).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

function monthName(m: number): string {
  return ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][m - 1];
}
